import { computed, type Ref, ref, watch } from 'vue';
import {
  AccountTypes,
  AssociationAccount,
  AssociationContact,
  OpportunityBase,
  OpportunityType,
  ValidationRule,
} from '@apparatix/types/Leads';
import { useOpportunityStore } from '@apparatix/stores/useOpportunityStore';

interface ErrorBag {
  accounts: string[];
  contacts: string[];
}

interface RuleSets {
  allCases: ValidationRule<ErrorBag>[];
  creatingLead: ValidationRule<ErrorBag>[];
  creatingOpportunity: ValidationRule<ErrorBag>[];
  convertingLead: ValidationRule<ErrorBag>[];
}

const PASS = true;
const FAIL = false;

export const useOpportunityAssociations = (
  type: OpportunityType,
  accounts: Ref<AssociationAccount[]>,
  contacts: Ref<AssociationContact[]>,
) => {
  const errorBag = ref<ErrorBag>({
    accounts: [],
    contacts: [],
  });

  const opportunityStore = useOpportunityStore();

  const canAssociateMoreAccounts = ref(true);

  const typeDescription = computed(() => `${opportunityStore.createOpportunityDisplayType === 'Lead' ? 'A' : 'An'} ${opportunityStore.createOpportunityDisplayType}`);

  const hasAccountType = (search: AssociationAccount[], accountType: AccountTypes) => Boolean(search?.find(account => account.c2RawType === accountType));

  const createOpportunityRules = computed<ValidationRule<ErrorBag>[]>(() => [
    {
      description: 'An opportunity must have a primary Contact',
      group: 'contacts',
      userMessage: 'An Opportunity must have a primary Contact',
      test: () => {
        if (!contacts.value?.length) {
          return FAIL;
        }
        const contactLabels = contacts.value.flatMap(contact => contact.contactLabels);
        if (!Array.isArray(contactLabels) || contactLabels.every(label => label === undefined)) {
          return FAIL;
        }
        if (contactLabels?.findIndex(label => label.contactLabelDisplayName === 'Primary') === -1) {
          return FAIL;
        }
        return PASS;
      },
    },
    {
      description: 'An opportunity must have an advertiser account',
      group: 'accounts',
      userMessage: 'An Opportunity must have an Advertiser Account',
      test: () => {
        if (!accounts.value?.length) {
          return FAIL;
        }
        if (accounts.value?.find(account => account.c2RawType === 'Advertiser')) {
          return PASS;
        }
        return FAIL;
      },
    },
  ]);

  const validationRuleSet = computed<RuleSets>(() => ({
    allCases: [
      {
        description: 'Can only have one type of each Account',
        group: 'accounts',
        userMessage: `${typeDescription.value} cannot have multiple Accounts of the same type`,
        test: () => {
          if (!accounts.value?.length) {
            return PASS;
          }
          const selectedTypes = accounts.value.map(account => account.c2RawType);
          if (!selectedTypes) {
            return PASS;
          }

          const typeSet = new Set(selectedTypes);
          if (typeSet.size === selectedTypes.length) {
            return PASS;
          }

          return FAIL;
        },
      },
      {
        description: 'Can only have one type of each Contact. Multiple contacts with no type are valid',
        group: 'contacts',
        userMessage: `${typeDescription.value} cannot have multiple Contacts of the same type`,
        test: () => {
          if (!contacts.value?.length) {
            return PASS;
          }

          const labelIds = contacts.value
            .filter(c => Array.isArray(c.contactLabels))
            .flatMap(c => c.contactLabels.map(l => l.contactLabelId));

          const counts = labelIds.reduce((acc, id) => {
            acc[id] = (acc[id] || 0) + 1;
            return acc;
          }, {});

          // fail only if primary or bill to is duplicated
          if ((counts[1] ?? 0) > 1 || (counts[3] ?? 0) > 1) {
            return FAIL;
          }

          return PASS;
        },
      },
      {
        description: 'Cannot have multiple advertiser-like accounts',
        group: 'accounts',
        userMessage: `${typeDescription.value} can only have one Advertiser Account`,
        test: () => {
          if (!accounts.value?.length) {
            return PASS;
          }
          const advertiserLikeAccounts = accounts.value.filter(
            account => account.c2RawType === 'Advertiser' ||
            account.c2RawType === 'Lead',
          );
          if (advertiserLikeAccounts?.length > 1) {
            return FAIL;
          }
          return PASS;
        },
      },
    ],
    creatingLead: [
      {
        description: 'If user adds account, one of them must be an unqualified advertiser or advertiser',
        group: 'accounts',
        userMessage: `${typeDescription.value} must have an Advertiser account`,
        test: () => {
          if (!accounts.value?.length) {
            return PASS;
          }
          if (accounts.value?.find(account => account.c2RawType === 'Advertiser') || accounts.value?.find(account => account.c2RawType === 'Lead')) {
            return PASS;
          }
          return FAIL;
        },
      },
    ],
    creatingOpportunity: createOpportunityRules.value,
    convertingLead: [
      createOpportunityRules.value[0],
      {
        description: 'Must have an advertiser account. Previously associated unqualified advertiser counts as valid',
        group: 'accounts',
        userMessage: 'An Opportunity must have an advertiser account',
        test: () => {
          if (!accounts.value?.length) {
            return FAIL;
          }
          if (accounts.value?.find(account => account.c2RawType === 'Advertiser') || accounts.value?.find(account => account.c2RawType === 'Lead')) {
            return PASS;
          }

          return FAIL;
        },
      },
    ],
  }));

  const areAccountsValid = computed(() => errorBag.value.accounts.length === 0);
  const areContactsValid = computed(() => errorBag.value.contacts.length === 0);

  const accountTypesValidForSelection = computed<AccountTypes[]>(() => {
    const hasAdvertiser = hasAccountType(accounts.value, 'Advertiser') || hasAccountType(accounts.value, 'Lead');
    const hasAgency = hasAccountType(accounts.value, 'Agency');
    const hasBillto =hasAccountType(accounts.value, 'Bill-To');

    canAssociateMoreAccounts.value = true;

    if (hasAdvertiser && hasAgency && hasBillto) {
      canAssociateMoreAccounts.value = false;
      return [];
    }
    if (hasAdvertiser && hasAgency && !hasBillto) {
      return ['Bill-To'];
    }
    if (hasAdvertiser && !hasAgency && hasBillto) {
      canAssociateMoreAccounts.value = false;
      return [];
    }
    if (hasAdvertiser && !hasAgency && !hasBillto) {
      return ['Bill-To'];
    }
    if (!hasAdvertiser && hasAgency && hasBillto) {
      return ['Advertiser'];
    }
    if (!hasAdvertiser && hasAgency && !hasBillto) {
      return ['Advertiser', 'Bill-To'];
    }
    if (!hasAdvertiser && !hasAgency && hasBillto) {
      return ['Advertiser', 'Agency'];
    }

    return ['Advertiser', 'Agency', 'Bill-To', 'Lead'];
  });

  const processRule = (rule: ValidationRule<ErrorBag>) => {
    const errorIndex = errorBag.value[rule.group].findIndex(error => error === rule.userMessage);
    const ruleAlreadyAdded = errorIndex !== -1;
    if (!rule.test()) {
      if (!ruleAlreadyAdded) {
        errorBag.value[rule.group].push(rule.userMessage);
      }
    } else {
      if (ruleAlreadyAdded) {
        errorBag.value[rule.group].splice(errorIndex, 1);
      }
    }
  };

  const getIndividualAccounts = () => [
    accounts.value?.find(account => account.c2RawType === 'Advertiser') ?? null,
    accounts.value?.find(account => account.c2RawType === 'Agency') ?? null,
    accounts.value?.find(account => account.c2RawType === 'Bill-To') ?? null,
    accounts.value?.find(account => account.c2RawType === 'Lead') ?? null,
  ];

  type HasAccountFields = Partial<OpportunityBase> & { agencyId?: number, billToId?: number };

  const addAssociationsToRequestBody = (opportunity: Partial<OpportunityBase>) => {
    const [advertiser, agency, billTo, unqualifiedAdvertiser] = getIndividualAccounts();
    if (advertiser) {
      opportunity.accountId = advertiser.c2Id;
    }
    if (agency) {
      (opportunity as HasAccountFields).agencyId = agency.c2Id;
    }
    if (billTo) {
      (opportunity as HasAccountFields).billToId = billTo.c2Id;
    }
    if (unqualifiedAdvertiser) {
      opportunity.accountId = unqualifiedAdvertiser.c2Id;
    }
  };

  watch([
    () => contacts.value,
    () => accounts.value,
    () => opportunityStore.createOpportunityContext,
  ], () => {
    errorBag.value.accounts = [];
    errorBag.value.contacts = [];
    if (!opportunityStore.createOpportunityContext) return;
    validationRuleSet.value.allCases.forEach(processRule);

    if (opportunityStore.createOpportunityContext === 'convert-lead') {
      validationRuleSet.value.convertingLead.forEach(processRule);
    }

    if (opportunityStore.createOpportunityContext === 'create-lead') {
      validationRuleSet.value.creatingLead.forEach(processRule);
    }

    if (opportunityStore.createOpportunityContext === 'create-opportunity') {
      validationRuleSet.value.creatingOpportunity.forEach(processRule);
    }

    if (opportunityStore.createOpportunityContext === 'editing') {
      if (type === 'Lead') {
        validationRuleSet.value.convertingLead.forEach(processRule);
      } else {
        validationRuleSet.value.creatingOpportunity.forEach(processRule);
      }
    }
  }, { deep: true, immediate: true });

  return {
    accountTypesValidForSelection,
    addAssociationsToRequestBody,
    areAccountsValid,
    areContactsValid,
    canAssociateMoreAccounts,
    errorBag,
    hasAccountType,
  };
};
